/**
 * RebuyModal — "out of chips" prompt shown mid-game when the local player's
 * table stack (bullet) is empty. One tap re-buys another bullet (the original
 * buy-in) from the bank; the server caps it to the remaining bank.
 *
 * The "auto re-buy" checkbox is read at RE-BUY time — tick it and future busts
 * re-buy silently (no modal). Body-level singleton so the game flow can open it
 * with `rebuyModal.show()` from anywhere.
 */

import { EventEmitter } from '../../engine/core/EventEmitter';

interface RebuyModalEvents {
  /** RE-BUY tapped. `auto` = the checkbox state (silence future prompts). */
  'rebuy': { auto: boolean };
  /** Dismissed without re-buying. */
  'dismiss': void;
}

const STYLE_ID = 'sdl-rebuy-styles';

export class RebuyModal extends EventEmitter<RebuyModalEvents> {
  public element: HTMLElement;
  private autoEl: HTMLInputElement;
  private visible = false;

  constructor() {
    super();
    RebuyModal.injectStyles();

    this.element = document.createElement('div');
    this.element.className = 'sdl-rebuy-host';
    this.element.innerHTML = `
      <div class="sdl-rebuy-backdrop"></div>
      <div class="sdl-rebuy-panel">
        <div class="sdl-rebuy-title">OUT OF CHIPS</div>
        <div class="sdl-rebuy-sub">Re-buy another bullet to stay in the game.</div>
        <button class="sdl-rebuy-btn" data-rebuy>RE-BUY</button>
        <label class="sdl-rebuy-auto">
          <input type="checkbox" data-auto>
          <span>Auto re-buy from now on</span>
        </label>
        <button class="sdl-rebuy-dismiss" data-dismiss>Maybe later</button>
      </div>
    `;
    document.body.appendChild(this.element);

    this.autoEl = this.element.querySelector('[data-auto]') as HTMLInputElement;

    this.element.querySelector('[data-rebuy]')!.addEventListener('click', () => {
      this.emit('rebuy', { auto: this.autoEl.checked });
      this.hide();
    });
    this.element.querySelector('[data-dismiss]')!.addEventListener('click', () => {
      this.emit('dismiss', undefined);
      this.hide();
    });
    this.element.querySelector('.sdl-rebuy-backdrop')!.addEventListener('click', () => {
      this.emit('dismiss', undefined);
      this.hide();
    });
  }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    this.element.classList.add('visible');
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.element.classList.remove('visible');
  }

  isVisible(): boolean {
    return this.visible;
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-rebuy-host {
        position: fixed; inset: 0;
        z-index: 700;
        display: none;
        align-items: center; justify-content: center;
      }
      .sdl-rebuy-host.visible { display: flex; }
      .sdl-rebuy-backdrop {
        position: absolute; inset: 0;
        background: rgba(0, 0, 0, 0.72);
        backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
      }
      .sdl-rebuy-panel {
        position: relative;
        width: 280px;
        padding: 26px 22px 18px;
        background: var(--concrete, #111114);
        border: 1px solid var(--glass-border, rgba(255,255,255,0.08));
        border-radius: 20px;
        display: flex; flex-direction: column; align-items: center;
        gap: 10px;
        box-shadow: 0 18px 50px rgba(0,0,0,0.6);
      }
      .sdl-rebuy-title {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 26px; letter-spacing: 2px;
        color: #fff;
      }
      .sdl-rebuy-sub {
        font-family: 'Rajdhani', sans-serif;
        font-size: 13px; text-align: center;
        color: rgba(255,255,255,0.55);
        margin-bottom: 6px;
      }
      .sdl-rebuy-btn {
        width: 100%;
        padding: 14px 0;
        border: none; border-radius: 14px;
        background: linear-gradient(135deg, #00FF88, #00b863);
        color: #06231a;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 20px; letter-spacing: 2px;
        cursor: pointer;
        box-shadow: 0 6px 18px rgba(0,255,136,0.25);
        transition: transform 0.1s;
      }
      .sdl-rebuy-btn:active { transform: scale(0.97); }
      .sdl-rebuy-auto {
        display: flex; align-items: center; gap: 8px;
        font-family: 'Rajdhani', sans-serif;
        font-size: 13px; color: rgba(255,255,255,0.7);
        cursor: pointer; user-select: none;
        margin-top: 4px;
      }
      .sdl-rebuy-auto input { width: 16px; height: 16px; accent-color: #00FF88; cursor: pointer; }
      .sdl-rebuy-dismiss {
        background: none; border: none;
        color: rgba(255,255,255,0.4);
        font-family: 'Rajdhani', sans-serif;
        font-size: 13px; cursor: pointer;
        padding: 4px;
      }
      .sdl-rebuy-dismiss:active { color: rgba(255,255,255,0.7); }
    `;
    document.head.appendChild(style);
  }
}
